import { ActivatedRoute } from '@angular/router';
import { AnalyticsUserProperties } from '@core-modules/analytics/enums/analytics-user-properties';
import { CoreAnalytics } from '@core-modules/analytics/providers/core-analytics';
import { CoreButtonStyle } from '@core-modules/components/core-button/core-button.component';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { NavController } from '@ionic/angular';
import { CoreConfiguration } from 'app/providers/core-configuration';
import { AuthProvider } from 'authorization/providers/auth/auth-provider';
import { MessageButton } from 'messages/pages/message/message.page.button';
import { MessagePageOptions } from 'messages/pages/message/message.page.data';
import { MessageIcon } from 'messages/pages/message/message.page.icons';
import { MessagePageController } from 'messages/providers/message-page-controller';
import { IntercomSupportProvider } from 'user-settings/providers/intercom-support';
import { LoginOutcome } from '../enums/login-outcome';
import { LoginEndResult } from '../models/login-end-result';

export class LoginComponent
{

    //#region Constructor

    /**
     * Class constructor
     */
    constructor
        (
            protected navCtrl: NavController,
            protected device: DeviceProvider,
            protected config: CoreConfiguration,
            protected route: ActivatedRoute,
            protected intercom: IntercomSupportProvider,
            protected analytics: CoreAnalytics,
            protected auth: AuthProvider,
            protected messagePageController: MessagePageController
        )
    {
    }

    //#endregion


    //#region Methods - MessagePage options

    /**
     * handles login outcome errors and Go to the message page
     * @param options 
     */
    protected handleErrorLoginOutcome(outcome: LoginOutcome, message: string, customerSupportEmailAddress: string)
    {
        switch (outcome)
        {
            case LoginOutcome.AccountLocked: {
                let options = this.getMessagePageOptions_AccountLocked(message, customerSupportEmailAddress);
                this.messagePageController.goToMessagePage(options);
                break;
            }
            case LoginOutcome.AccountDoesNotExist: {
                let options = this.getMessagePageOptions_AccountDoesNotExist(message);
                this.messagePageController.goToMessagePage(options);
                break;
            }
            case LoginOutcome.AccountInvalid: {
                let options = this.getMessagePageOptions_AccountInvalid(message, customerSupportEmailAddress);
                this.messagePageController.goToMessagePage(options);
                break;
            }
            case LoginOutcome.InvalidToken: {
                let options = this.getMessagePageOptions_InvalidToken(message);
                this.messagePageController.goToMessagePage(options);
                break;
            }
            case LoginOutcome.InvalidLoginMethod: {
                let options = this.getMessagePageOptions_LoginMethodInvalid(customerSupportEmailAddress);
                this.messagePageController.goToMessagePage(options);
                break;
            }
            case LoginOutcome.UserCancelled: {
                this.goToLandingPage();
                break;
            }
            default:
                throw ("Invalid login outcome");


        }
    }

    /**
     * Get the message page configuration for when an account is locked
     * @param message 
     * @param customerSupportEmailAddress 
     */
    private getMessagePageOptions_AccountLocked(message: string, customerSupportEmailAddress: string): MessagePageOptions
    {
        let buttons:MessageButton[] = [
            {
                text: 'Email support',
                style: CoreButtonStyle.Solid,
                action: () => { this.device.sendEmail(customerSupportEmailAddress, 'Core Account Locked'); }
            },
            {
                text: 'Open Live Chat',
                style: CoreButtonStyle.Solid,
                action: () => 
                {
                    this.intercom.showConversations();
                }
            },
            {
                text: 'Continue',
                style: CoreButtonStyle.Solid,
                action: () =>
                {
                    this.goToLandingPage();
                }
            }];

        return {
            icon: MessageIcon.LOCK,
            messageExclaimation: 'Oops!',
            messageTagline: 'Your account is locked!',
            messageDescription: message,
            buttons: buttons
        };
    }

    /**
     * Get the message page configuration for when an account is locked
     * @param message 
     * @param customerSupportEmailAddress 
     */
    private getMessagePageOptions_LoginMethodInvalid(customerSupportEmailAddress: string): MessagePageOptions
    {
        let buttons = [
            {
                text: 'Continue',
                style: CoreButtonStyle.Solid,
                action: () =>
                {
                    this.goToLandingPage();
                }
            }];

        return {
            icon: MessageIcon.LOCK,
            messageExclaimation: 'Oops!',
            messageTagline: 'This option is not available!',
            messageDescription: `You cannot access this account by pin or biometrics. Tap below to sign in.`,
            buttons: buttons
        };
    }

    /**
     * Get the message page configuration for when an account
     * does not exist
     * @param message 
     */
    private getMessagePageOptions_AccountDoesNotExist(message: string): MessagePageOptions
    {
        let buttons = [
            {
                text: 'Continue',
                style: CoreButtonStyle.Solid,
                action: () =>
                {
                    this.goToLandingPage();
                }
            }];

        return {
            icon: MessageIcon.GENERIC,
            messageExclaimation: 'Oops!',
            messageTagline: `We couldn't find your account!`,
            messageDescription: message,
            buttons: buttons
        };
    }

    /**
     * Get the message page options for an invalid account outcome
     * @param message 
     * @param customerSupportEmailAddress 
     */
    private getMessagePageOptions_AccountInvalid(message: string, customerSupportEmailAddress: string): MessagePageOptions
    {
        let buttons : MessageButton[]= [
            {
                text: 'Email Support',
                style: CoreButtonStyle.Solid,
                action: () => { this.device.sendEmail(customerSupportEmailAddress, 'Problem Logging Into Core (Mobile)'); }
            },
            {
                text: 'Continue',
                style: CoreButtonStyle.Solid,
                action: () =>
                {
                    this.goToLandingPage();
                }
            }
        ];


        return {
            icon: MessageIcon.CROSS,
            messageExclaimation: 'Oops!',
            messageTagline: 'We could not sign you in!',
            messageDescription: message,
            buttons: buttons
        };
    }

    /**
     * Get the message page configuration for when an account
     * does not exist
     * @param message 
     */
    private getMessagePageOptions_InvalidToken(message: string): MessagePageOptions
    {
        let buttons: MessageButton[] = [
            {
                text: 'Continue',
                style: CoreButtonStyle.Solid,
                action: () =>
                {
                    this.goToLandingPage();
                }
            }];

        return {
            icon: MessageIcon.INFO,
            messageExclaimation: 'Oops!',
            messageTagline: 'You need sign in again!',
            messageDescription: message,
            buttons: buttons
        };
    }

    //#endregion

    //#region Methods - Application

    /**
    * Proceed down the application login pipeline
    * @param result 
    */
    protected async proceedToApplication(result: LoginEndResult)
    {
        this.analytics.SetUserProperty(AnalyticsUserProperties.DemoAccount, `${result.IsDemoAccount}`);
        this.analytics.TrackEvent('login');
        if (this.device.isApplication() && !result.HasPinNumber && !result.IsDemoAccount)
        {
            this.navCtrl.navigateRoot('/check-pin');
        } else
        {
            this.navCtrl.navigateRoot('/home/' + result.SessionToken);
        }
    }

    /**
     * Go back to the Landing Page
     */
    private async goToLandingPage()
    {
        await this.config.clearConfiguration();
        this.navCtrl.navigateRoot('/landing');;
    }

    //#endregion
}
